import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { warnAndNotify } from '../../../../../../Honeybadger';
import { useLearningCollection } from '../LearningCollectionContext';
import { AllExperiencesResource } from '../Types';
import { FilterStores } from './FilterStores';
import { useFilterOptions } from './useFilterOptions';
import { FilterOptionsPayload } from './FilterOptionsTypes';
import { persistentFilterStore } from './PersistentFilterStore';
import {
  calculateProductTypeOptions,
  calculateSchoolPartnerOptions,
  calculateLanguageOptions,
  calculateDurationOptions,
  calculateLicenseOptions,
  calculateCompetencyOptions,
} from './Utils';

const emptyFilterOptions: FilterOptionsPayload = {
  product_type_options: [],
  duration_options: [],
  school_partner_options: [],
  language_options: [],
  license_options: [],
  show_unlicensed_toggle: 'hide',
};

interface Props {
  resource: AllExperiencesResource;
  children: React.ReactNode;
}

const FilterContext: React.FC<Props> = ({ resource, children }) => {
  const experiences = resource.payload.experiences;
  // Get the learning collection template resource which has filter_options link with product_license_id
  const { resourceState } = useLearningCollection();

  // Get server-provided filter options using the template resource links (includes product_license_id)
  const { data: filterOptions, isLoading, isError, error } = useFilterOptions(resourceState.links);

  // Alert Honeybadger if filter options fail to load
  useEffect(() => {
    if (isError) {
      warnAndNotify('FilterOptionsLoadError', 'Failed to load filter options for BuilderV2', {
        error,
      });
    }
  }, [isError, error]);

  // If filter options are still loading, hide everything until they arrive
  if (isLoading) {
    return <FilterStores.Provider value={nothing()}>{children}</FilterStores.Provider>;
  }

  // Fall back to empty options when the filter_options link is missing or the request errors,
  // so the experience list still renders rather than going blank
  const resolvedFilterOptions = filterOptions ?? emptyFilterOptions;

  // Calculate filter options using server options as source of truth
  const filterOptionsMemo = {
    productTypeOptions: calculateProductTypeOptions(experiences, resolvedFilterOptions),
    schoolPartnerOptions: calculateSchoolPartnerOptions(experiences, resolvedFilterOptions),
    languageOptions: calculateLanguageOptions(experiences, resolvedFilterOptions),
    durationOptions: calculateDurationOptions(experiences, resolvedFilterOptions),
    licenseOptions: calculateLicenseOptions(experiences, resolvedFilterOptions),
    competencyOptions: calculateCompetencyOptions(experiences),
  };

  const {
    productTypeOptions,
    schoolPartnerOptions,
    languageOptions,
    durationOptions,
    licenseOptions,
    competencyOptions,
  } = filterOptionsMemo;

  // Get or create filter stores from the persistent store
  // This maintains filter state across component unmounts/remounts
  const stores = persistentFilterStore.getOrCreateStores(
    productTypeOptions,
    schoolPartnerOptions,
    languageOptions,
    durationOptions,
    licenseOptions,
    competencyOptions,
  );

  const filterContextValue = {
    productTypeFilterStore: stores.productTypeFilterStore,
    durationFilterStore: stores.durationFilterStore,
    schoolPartnerFilterStore: stores.schoolPartnerFilterStore,
    languageFilterStore: stores.languageFilterStore,
    licenseFilterStore: stores.licenseFilterStore,
    competencyFilterStore: stores.competencyFilterStore,
    sortStore: stores.sortStore,
    productTypeOptions,
    schoolPartnerOptions,
    languageOptions,
    durationOptions,
    licenseOptions,
    competencyOptions,
    unlicensedToggleStore: stores.unlicensedToggleStore,
    showUnlicensedToggle: resolvedFilterOptions.show_unlicensed_toggle,
  };

  return <FilterStores.Provider value={just(filterContextValue)}>{children}</FilterStores.Provider>;
};

export default observer(FilterContext);
